import type { PermsContextType } from 'contexts/perms.types';
import {
  ACTION_CATEGORY_PERMISSIONS,
  ADMIN_BYPASS_ACCESS_LEVELS,
  GLOBAL_ACTION_PERMISSION_CODE,
} from '../config/globalActionPermissions.config';

/**
 * The permission rules of the builder action categories. The front end mirrors the backend
 * Global Action permission middleware: a category that the user may not use is hidden, an existing
 * row of that category is read-only, and the save stops before the request.
 */

/** The part of the perms context that these checks read. */
export type ActionPermissionTester = Pick<PermsContextType, 'testPermission' | 'accessLevel'>;

/** A builder row: only the category is read. */
export type ActionPermissionRow = { category?: string | null };

/** Tell if the user may use a category. A category with no rule is always allowed. */
export const canUseActionCategory = (
  perms: ActionPermissionTester,
  category: string | null | undefined,
): boolean => {
  if (!category) return true;
  const rule = ACTION_CATEGORY_PERMISSIONS[category];
  if (!rule) return true;
  // A non-strict key passes for the backend's admin levels without the key (the middleware's
  // admin bypass). A strict key (CreateFemaAlert) never does.
  if (!rule.strict && ADMIN_BYPASS_ACCESS_LEVELS.includes(Number(perms.accessLevel))) return true;
  return perms.testPermission(rule.levels, [rule.permission]);
};

/**
 * The permission keys that the user does not have for the builder actions. Each key shows once,
 * in the order of the builder rows.
 */
export const missingActionPermissions = (
  perms: ActionPermissionTester,
  actions: readonly ActionPermissionRow[] | null | undefined,
): string[] => {
  const missing: string[] = [];
  for (const action of actions ?? []) {
    const category = action?.category;
    if (!category) continue;
    const rule = ACTION_CATEGORY_PERMISSIONS[category];
    if (!rule || missing.includes(rule.permission)) continue;
    if (!canUseActionCategory(perms, category)) missing.push(rule.permission);
  }
  return missing;
};

/** The notice for a save or an enable that needs permissions the user does not have. */
export const actionPermissionNotice = (missing: readonly string[]): string =>
  missing.length > 0
    ? `This Global Action needs a permission you do not have: ${missing.join(', ')}. Ask an administrator to grant it.`
    : 'This Global Action needs a permission you do not have. Ask an administrator to grant it.';

/** The notice above one builder row that the user may see but not change. */
export const actionRowPermissionNotice = (permission: string): string =>
  `This action needs the ${permission} permission. You can see it, but you cannot change it or save this Global Action.`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Read the `missing` list from a refusal body. Null when the body is not the permission refusal. */
const readRefusalBody = (body: unknown): string[] | null => {
  if (!isRecord(body) || body.code !== GLOBAL_ACTION_PERMISSION_CODE) return null;
  const { missing } = body;
  if (!Array.isArray(missing)) return [];
  return missing.filter((key): key is string => typeof key === 'string');
};

/**
 * Read the backend 403 `GLOBAL_ACTION_PERMISSION_REQUIRED` refusal of a create, an update or an
 * enable. The body is read from `raw` (axiosBaseQuery) or `data`, as `readBackendError` reads it.
 * @returns The missing permission keys, or null when the error is not this refusal.
 */
export const readGlobalActionPermissionRefusal = (error: unknown): string[] | null => {
  if (!isRecord(error) || error.status !== 403) return null;
  return readRefusalBody(error.raw ?? error.data);
};
